import {
  afterNextRender,
  Component,
  ElementRef,
  inject,
  Injector,
  runInInjectionContext,
  signal,
  viewChild,
} from '@angular/core';
import { UserService } from '../services/api-services/user.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent, CuiDialogService } from '@innersource/common-ui-components';
import { NotificationDialComponent } from '../utils/dialogues/notification-dial/notification-dial.component';
import { CapabilityStore } from '../services/capability.store';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { MatMiniFabButton, MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { ChangePwdFormComponent } from './change-pwd-form/change-pwd-form.component';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  imports: [
    MatDivider,
    MatIcon,
    FormsModule,
    MatMiniFabButton,
    MatTooltip,
    MatIconButton,
    ChangePwdFormComponent,
    MatButton,
  ],
})
export class SettingsComponent {
  /** Inject services. */
  private readonly userService = inject(UserService);
  private readonly dialogRef = inject(MatDialog);
  private readonly confirmationDialog = inject(CuiDialogService);
  private readonly capabilityStore = inject(CapabilityStore);
  private readonly injector = inject(Injector);

  /** The user info. */
  userInfo = this.userService.user;

  /** A potential new user name (synced with the input) */
  newUsername = signal<string | null>(null);

  /** The username input. */
  readonly userNameField = viewChild<ElementRef<HTMLInputElement>>('userNameField');

  /**
   * Toggles the username edit field.
   */
  toggleUserNameEdit() {
    const userInfo = this.userInfo();
    if (userInfo && this.newUsername() === null) {
      this.newUsername.set(userInfo.userName);
      runInInjectionContext(this.injector, () => {
        afterNextRender(() => {
          this.userNameField()?.nativeElement.focus();
        });
      });
    } else {
      this.newUsername.set(null);
    }
  }

  /**
   * Start a data deletion request.
   */
  requestDataDelete() {
    this.confirmationDialog.open(ConfirmationDialogComponent, {
      size: 'small',
      ariaLabel: 'Delete Data',
      data: {
        confirmTitle: 'Delete Data',
        confirmMessage:
          'Are you sure you want to delete all job data? This action cannot be undone.',
        onConfirm: async () => {
          const res = await this.capabilityStore.deleteAllJobs();
          this.showNotification(res.msg, res.success ? 'Successful' : 'Error');
        },
      },
    });
  }

  private showNotification(msg: string, title: string = '') {
    this.dialogRef.open(NotificationDialComponent, {
      data: { title: title ?? 'Error', message: msg },
    });
  }

  /**
   * Apply a user renaming.
   */
  async applyChanges() {
    const currentInfo = this.userInfo();
    if (!currentInfo) return;

    // Just hide the input field if nothing happened.
    const userName = this.newUsername();
    if (userName === null || userName === currentInfo.userName) {
      this.newUsername.set(null);
      return;
    }

    // Send the new info to the server.
    const updatedUser = { ...currentInfo, userName };
    const res = await this.userService.updateUserName(updatedUser);
    if (res.status === 'success') {
      const ref = this.dialogRef.open(NotificationDialComponent, {
        data: {
          title: 'Update successful',
          message: 'Username has been successfully changed.',
        },
      });
      ref.afterClosed().subscribe(() => {
        this.toggleUserNameEdit();
      });
    } else {
      this.showNotification(
        'An error occurred while changing the username. ' +
          'If you need help, please contact the support.',
        res.msg
      );
    }
  }
}
